'use client';

import { gql, useMutation } from '@apollo/client';
import { ArrowRight, CheckCircle, Download, FileDown } from 'lucide-react';
import { useState } from 'react';

const SUBSCRIBE_MUTATION = gql`
  mutation SubscribeEmailToNewsletter($email: String!, $from: Int) {
    subscribeEmailToNewsletter(email: $email, from: $from) {
      status
    }
  }
`;

const PDF_URL = 'https://www.kingsyard.com/media/wysiwyg/Kingsyard%20Birding%20Guide%202025.pdf';

const EMAIL_REGEX = /^[A-Za-z0-9]+([-_.][A-Za-z0-9]+)*@([A-Za-z0-9]+[-.])+[A-Za-z]{2,5}$/;

export const DownloadForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [serverError, setServerError] = useState('');
  const [subscribe, { loading }] = useMutation(SUBSCRIBE_MUTATION, {
    fetchPolicy: 'no-cache',
    onError: err => {
      if (err.message.toLowerCase().includes('already')) {
        setSubmitted(true);
      } else {
        setServerError(err.message);
      }
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError('');
    setServerError('');
    const form = e.currentTarget;
    const email = (new FormData(form).get('email') as string).trim();

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    try {
      await subscribe({ variables: { email, from: 2 } });
      setSubmitted(true);
    } catch (e) {
      // error handled by onError
    }
  };

  return (
    <div className='w-full'>
      <div className='rounded-xl border border-[#e8e6e2] bg-white p-8 shadow-[0_2px_12px_rgba(0,0,0,0.06)]'>
        <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#eaf2eb]'>
          <Download className='h-7 w-7 text-[#1a1a1a]' strokeWidth={1.5} />
        </div>
        <h3 className='mb-2 text-center text-[20px] font-bold text-[#1a1a1a]'>Download the Complete Guide</h3>
        <p className='mb-6 text-center text-[15px] text-[#666]'>
          Get the full PDF version with bonus feeding charts and seasonal tips.
        </p>

        {submitted ? (
          <div className='py-5 text-center'>
            <CheckCircle className='mx-auto mb-3 h-12 w-12 text-primary' strokeWidth={1.5} />
            <h4 className='mb-2 text-[18px] font-bold text-[#1a1a1a]'>Your Guide is Ready!</h4>
            <p className='mb-4 text-sm text-[#666]'>Click the button below to download your complete birding guide.</p>
            <a
              href={PDF_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3.5 font-[Arial,sans-serif] text-[15px] font-semibold leading-[17px] text-white no-underline transition-colors hover_bg-[#245027]'
            >
              <FileDown className='h-4 w-4' />
              Download PDF Guide
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className='flex flex-col gap-3'>
              <div>
                <input
                  type='email'
                  name='email'
                  required
                  placeholder='Enter your email address'
                  className='w-full rounded-lg border-2 border-[#e8e6e2] px-4 py-3.5 text-[15px] leading-[normal] text-[#000] outline-none transition-colors placeholder_text-[#757575] focus_border-primary'
                />
                {emailError && <p className='mt-1 text-left text-[13px] text-red-600'>{emailError}</p>}
              </div>
              <button
                type='submit'
                disabled={loading}
                className='inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border-none bg-primary px-6 py-3.5 font-[Arial,sans-serif] text-[15px] font-semibold leading-[17px] text-white transition-colors hover_bg-[#245027] disabled_opacity-60'
              >
                {loading ? 'Submitting...' : 'Get Download Link'}
                {!loading && <ArrowRight className='h-4 w-4' />}
              </button>
            </div>
            {serverError && <p className='mt-3 text-center text-[13px] text-red-600'>{serverError}</p>}
            <p className='mt-3 text-center text-[13px] text-[#666]'>We respect your privacy. Unsubscribe anytime.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default DownloadForm;
